import type { ComponentType } from "react";
import type { FEATURES } from "types/landlord/partner";
import { PaywallScreen } from "./PaywallScreen";
import { useIsCrmFeatureGated } from "./hooks";
import type { PaywallFeature } from "./constants";

/**
 * Wraps a page component so a partner without `feature` sees the upgrade prompt
 * instead of the page.
 *
 * Applied at the route definition rather than inside each view: the surfaces
 * that need it (showings, lockboxes, phone numbers and the four showings
 * settings pages) have nothing else to do with the paywall, and a wrapper is
 * what keeps their queries from firing while locked — an early return inside the
 * view would still have run every hook above it and spent a round of 403s the
 * paywall then hides.
 *
 * Dropping the route instead (the old behaviour) sent these paths to
 * NotFoundView, so a bookmark or a deep link landed on a bare "Not found" with
 * nothing to click (CRM-759).
 */
export const withCrmAccessGate = (
  useIsGated: () => boolean,
  paywall: PaywallFeature,
  Component: ComponentType
) => {
  const Gated = () => {
    const isGated = useIsGated();
    if (isGated) return <PaywallScreen feature={paywall} />;

    return <Component />;
  };
  Gated.displayName = `withCrmAccessGate(${paywall})`;

  return Gated;
};

export const withCrmFeatureGate = (
  feature: FEATURES,
  paywall: PaywallFeature,
  Component: ComponentType
) => withCrmAccessGate(() => useIsCrmFeatureGated(feature), paywall, Component);
